import { useBoolean, useToast } from '@chakra-ui/react'
import * as crypto from 'crypto-js'
import fileDownload from 'js-file-download'
import { createContext, FC, useCallback, useMemo, useState } from 'react'
import { AiOutlineBarChart } from 'react-icons/ai'
import Swal from 'sweetalert2'
import { useImmer } from 'use-immer'

import useSessions from '@hook/shared/use-sessions'

import { actionsHeaderOtb } from '@helper/otb/plan/actionsHeaderOtb'
import { paramsToSendMapping } from '@helper/otb/plan/paramsToSendMapping'
import { parsePlanOtbData } from '@helper/otb/plan/parsePlanOtbData'
import { parsePlanOtbDataFooter } from '@helper/otb/plan/parsePlanOtbDataFooter'
import { parsePlanOtbExportData } from '@helper/otb/plan/parsePlanOtbExportData'
import { parseUpdateFinalStorage } from '@helper/otb/plan/parseUpdateFinalStorage'
import { validateGroupName } from '@helper/otb/plan/validateGroupName'
import { validateIfShowColumnInTable } from '@helper/otb/plan/validateIfShowColumnInTable'
import { encrypteHeaderStorange } from '@helper/util/cryptoStorange'
import { validateAppVersion } from '@helper/util/storangeClear'
import generic from '@service/generic'
import otb from '@service/otb/plan'

import { ILayoutFilter } from '@base/@types/components/Layout/Filters'
import {
  ITableGridAction,
  ITableHeader
} from '@base/@types/components/Layout/TableGrid'
import { ILayoutToolsAction } from '@base/@types/components/Layout/Tools'
import {
  IFinalStorageRequest,
  IOtbPlanContext,
  IParamsToSend,
  IUpdateParams
} from '@base/@types/providers/Otb/PlanProvider'
import { planFieldsFilter } from '@base/variables/headers/otb/plan/planFieldsFilter'
import { planIndicatorsRequest } from '@base/variables/headers/otb/plan/planIndicatorsRequest'
import { planOtbHeader } from '@base/variables/headers/otb/plan/planOtbHeader'

const OtbPlanProvider: FC = ({ children }) => {
  const toast = useToast()
  const { register } = useSessions()
  const filtersList: ILayoutFilter[] = planFieldsFilter

  const currentMonth = useMemo(() => new Date().getMonth() + 1, [])
  const currentYear = useMemo(() => new Date().getFullYear(), [])

  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [loadingUpdateGrid, setLoadingUpdateGrid] = useState(false)
  const [fetching, _setFetching] = useState(false)
  const [filters, setFilters] = useBoolean(true)
  const [filename, setFilename] = useState('OTB Plano')
  const [data, updateData] = useImmer<any[]>([])
  const [dataUpdate, updateDataUpdate] = useImmer<any[]>([])
  const [headers, updateHeaders] = useImmer<ITableHeader[]>(planOtbHeader)
  const [finalStorage, updateFinalStorage] = useState<IFinalStorageRequest>({
    planos: []
  })

  const [params, updateParams] = useImmer<any>(() => {
    const paramsFormatted = { ...planIndicatorsRequest }

    paramsFormatted.periodoSelectIds = {
      ano: currentYear,
      mes: currentMonth,
      descricao: `${currentMonth}/${currentYear}`,
      label: `1 - ${currentMonth}/${currentYear}`,
      value: 1
    }

    return paramsFormatted
  })

  const [paramsToSend, setParamsToSend] = useState<IParamsToSend>(() => {
    const paramsToSendFormatted = { ...planIndicatorsRequest }

    paramsToSendFormatted.periodo = {
      ano: currentYear,
      mes: currentMonth,
      descricao: ''
    }

    return paramsToSendFormatted
  })

  const handleUpdateFinalStorage = (value: string, data: any) => {
    const currentMonth = new Date().getMonth()
    const month = data['empresa-mes']

    if (Number(month) !== currentMonth + 1) {
      const newPlan = parseUpdateFinalStorage(value, data)

      const verifyIfExistPlan = finalStorage.planos.filter(
        plan => plan.empresaId === data['empresa-id']
      )

      if (verifyIfExistPlan.length === 0) {
        updateFinalStorage({
          planos: [...finalStorage.planos, newPlan]
        })
      } else {
        const removePlanAlreadyExists = finalStorage.planos.filter(
          plan => plan.empresaId !== data['empresa-codigo-id']
        )

        updateFinalStorage({
          planos: [...removePlanAlreadyExists, newPlan]
        })
      }
    }
  }

  const handleUpdateGrowthWanted = useCallback(
    async (index: number, wanted: number | string, isDisabled = false) => {
      const formattedData = data.map((item, index) => {
        return {
          ...item,
          id: index + 1
        }
      })

      let planOtbData = formattedData

      const planOtb = planOtbData.filter(plan => plan.id === Number(index))[0]
      const idData = planOtbData.findIndex(
        (plan: any) => plan.id === Number(index)
      )

      planOtbData = [...planOtbData]

      planOtbData[idData] = {
        ...planOtb,
        estoqueFinalAjustado: wanted,
        disabled: isDisabled
      }
      updateDataUpdate([...dataUpdate, idData])
      updateData(draft => {
        return draft.filter(item => item.status === 99).concat(planOtbData)
      })
    },
    [data, dataUpdate, updateData, updateDataUpdate]
  )

  const handleSaveUpdateGrid = () => {
    setLoadingUpdateGrid(true)
    const uniqueIndexs = dataUpdate.filter((v, i, a) => a.indexOf(v) === i)
    const datasUpdates: any = []
    const auxCount = 0
    uniqueIndexs.forEach(index => {
      if (auxCount === 0) {
        datasUpdates.push(data[index])
      }
    })
    otb
      .putOtbFinalStorageAdjustedData({ planos: datasUpdates })
      .then(() => {
        setLoadingUpdateGrid(false)

        toast({
          title: 'Sucesso.',
          description: 'Seus parâmetros foram atualizados',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right'
        })

        updateFinalStorage({
          planos: []
        })
      })
      .catch(({ response: { data } }) => {
        setLoadingUpdateGrid(false)

        const message =
          data.response?.status === 400
            ? data.response?.data?.data[0].message
            : 'Ocorreu um problema interno. Tente novamente mais tarde'
        toast({
          title: 'Atenção.',
          description: message,
          status: 'warning',
          duration: 3000,
          isClosable: true
        })

        updateFinalStorage({
          planos: []
        })
      })
  }

  const clearAllFilters = () => {
    const paramsFormatted = { ...planIndicatorsRequest }

    paramsFormatted.periodo = {
      ano: currentYear,
      mes: currentMonth,
      descricao: `${currentMonth}/${currentYear}`
    }

    updateParams({
      ...params,
      empresaSelectIds: [],
      mundoSelectIds: [],
      categoriaCompactaSelectIds: [],
      categoriaSelectIds: [],
      periodoSelectIds: {
        ano: currentYear,
        mes: currentMonth,
        descricao: `${currentMonth}/${currentYear}`,
        label: `1 - ${currentMonth}/${currentYear}`,
        value: 1
      }
    })

    setParamsToSend(paramsFormatted)
    if (localStorage.getItem('otbPlanParams')) {
      localStorage.setItem(
        'otbPlanParams',
        crypto.AES.encrypt(
          JSON.stringify({
            ...params,
            empresaSelectIds: [],
            mundoSelectIds: [],
            categoriaCompactaSelectIds: [],
            categoriaSelectIds: [],
            periodoSelectIds: {
              ano: currentYear,
              mes: currentMonth,
              descricao: `${currentMonth}/${currentYear}`,
              label: `1 - ${currentMonth}/${currentYear}`,
              value: 1
            }
          }),
          'CHAVE'
        ).toString()
      )
    }
    if (localStorage.getItem('otbPlanParamsRequest')) {
      localStorage.setItem(
        'otbPlanParamsRequest',
        crypto.AES.encrypt(JSON.stringify(paramsFormatted), 'CHAVE').toString()
      )
    }
  }

  const handleUpdateParams = ({ name, value }: IUpdateParams) => {
    updateParams((draft: { [x: string]: string }) => {
      draft[name] = value
    })

    const paramsToSendFormatted = paramsToSendMapping({
      name,
      compareName: name,
      values: name === 'periodoSelectIds' ? [value] : value,
      paramsToSend,
      fieldName: validateGroupName(name)
    })

    setParamsToSend(paramsToSendFormatted)
    if (localStorage.getItem('otbPlanParams')) {
      const newParams: IParamsToSend[] = JSON.parse(
        crypto.AES.decrypt(
          localStorage.getItem('otbPlanParams'),
          'CHAVE'
        ).toString(crypto.enc.Utf8) || ''
      )

      localStorage.setItem(
        'otbPlanParams',
        crypto.AES.encrypt(
          JSON.stringify({ ...newParams, [name]: value }),
          'CHAVE'
        ).toString()
      )
    }
    if (localStorage.getItem('otbPlanParamsRequest')) {
      localStorage.setItem(
        'otbPlanParamsRequest',
        crypto.AES.encrypt(
          JSON.stringify(paramsToSendFormatted),
          'CHAVE'
        ).toString()
      )
    }
  }

  const handleLocalParams = useCallback(async () => {
    if (localStorage.getItem('otbPlanParams')) {
      const newParams = JSON.parse(
        crypto.AES.decrypt(
          localStorage.getItem('otbPlanParams'),
          'CHAVE'
        ).toString(crypto.enc.Utf8) || ''
      )
      updateParams(newParams)
    } else {
      localStorage.setItem(
        'otbPlanParams',
        crypto.AES.encrypt(JSON.stringify(params), 'CHAVE').toString()
      )
    }
    if (localStorage.getItem('otbPlanParamsRequest')) {
      const newParams = JSON.parse(
        crypto.AES.decrypt(
          localStorage.getItem('otbPlanParamsRequest'),
          'CHAVE'
        ).toString(crypto.enc.Utf8) || ''
      )
      setParamsToSend(newParams)
    } else {
      localStorage.setItem(
        'otbPlanParamsRequest',
        crypto.AES.encrypt(JSON.stringify(paramsToSend), 'CHAVE').toString()
      )
    }
  }, [updateParams, setParamsToSend, params, paramsToSend])

  const tableInteraction = useMemo<ITableGridAction[]>(() => {
    return [
      {
        type: 'default',
        icon: <AiOutlineBarChart />,
        onSelect: () => []
      }
    ]
  }, [])

  const handleHeaderLocalStorange = useCallback(() => {
    if (!localStorage.getItem('@OtbPlanColumnsTable:')) {
      encrypteHeaderStorange('@OtbPlanColumnsTable:', headers)
    } else {
      localStorage.removeItem('@OtbPlanColumnsTable:')
      encrypteHeaderStorange('@OtbPlanColumnsTable:', headers)
    }
  }, [headers])

  const toggleColumn = useCallback(
    (index: number, column: number) => {
      const newHeaders = [...headers]

      newHeaders[index].columns[column] = {
        id: newHeaders[index].columns[column].id,
        name: newHeaders[index].columns[column].name,
        show: !newHeaders[index].columns[column].show,
        notShowColumn: newHeaders[index].columns[column].notShowColumn
          ? !newHeaders[index].columns[column].notShowColumn
          : true
      }

      updateHeaders(newHeaders)
      handleHeaderLocalStorange()
    },
    [handleHeaderLocalStorange, headers, updateHeaders]
  )

  const actions = useMemo<ILayoutToolsAction[]>(() => {
    return actionsHeaderOtb({
      data,
      headers,
      toggleColumn,
      parsePlanOtbData
    })
  }, [toggleColumn, data, headers])

  const updateShowTableInTable = useCallback(
    async (data: any[]) => {
      updateData(draft => {
        return draft.filter(item => item.status === 99).concat(data)
      })

      const headersFormatted = validateIfShowColumnInTable(
        headers,
        paramsToSend
      )

      updateHeaders(headersFormatted)
    },
    [updateData, updateHeaders, headers, paramsToSend]
  )
  const getData = () => {
    setLoading(true)
    getMaintence()
    localStorage.setItem(
      '@otbPlanParamsToSend:',
      crypto.AES.encrypt(JSON.stringify(paramsToSend), 'CHAVE').toString()
    )

    otb
      .postOtbPlansData(paramsToSend)
      .then(({ data: { data } }) => {
        updateShowTableInTable(data)

        localStorage.setItem(
          '@otbPlanParamsResponse:',
          crypto.AES.encrypt(JSON.stringify(data), 'CHAVE').toString()
        )

        register({
          name: 'OTB Plano',
          path: '/otb/plano',
          loading: false
        })

        setFilters.toggle()
        setLoading(false)
      })
      .catch(() => {
        updateData([])
        setLoading(false)
      })
  }

  const exportKpiData = useCallback(() => {
    Swal.fire({
      title: 'Exporta dados!',
      html: 'Aguarde um momento<b></b>.',
      timerProgressBar: true,
      timer: 99999,
      didOpen: () => {
        Swal.showLoading()
      }
    })
    const paramsToSendFormatted = parsePlanOtbExportData(headers, paramsToSend)

    otb
      .postOtbPlansExportData(paramsToSendFormatted)
      .then(({ data }) => {
        fileDownload(data, filename + '.csv')
        Swal.close()
      })
      .catch(({ response: { data } }) => {
        Swal.close()
        toast({
          title: 'Erro Requisição.',
          description:
            data.message ||
            'Ocorreu um problema interno. Tente novamente mais tarde',
          status: 'error',
          duration: 2000,
          isClosable: true
        })
      })
  }, [filename, headers, paramsToSend, toast])

  const getMaintence = useCallback(async () => {
    generic
      .getMaintence('manutencao')
      .then(({ data: { data } }) => {
        validateAppVersion(data.version)

        if (data.valor === 'true') {
          return (window.location.href = '/manutencao')
        }
      })
      .catch(() => {})
  }, [])

  return (
    <OtbPlanContext.Provider
      value={{
        data: parsePlanOtbData(data),
        query,
        params,
        headers,
        filters,
        loading,
        actions,
        fetching,
        filename,
        filtersList,
        finalStorage,
        tableInteraction,
        loadingUpdateGrid,
        getData,
        setQuery,
        setFilters,
        setFilename,
        exportKpiData,
        clearAllFilters,
        handleUpdateParams,
        handleSaveUpdateGrid,
        handleUpdateFinalStorage,
        handleLocalParams,
        handleUpdateGrowthWanted,

        dataFooter: parsePlanOtbDataFooter(data)
      }}
    >
      {children}
    </OtbPlanContext.Provider>
  )
}

export default OtbPlanProvider
export const OtbPlanContext = createContext<IOtbPlanContext>(
  {} as IOtbPlanContext
)
